// sites.api.ts — T049
// Clients HTTP sites et régions.
// Contrats : specs/001-duerp-gestion-risques/contracts/sites.md

import type {
  PaginatedResponse,
  PaginationInfo,
} from "@/domain/types/pagination";
import type { RegionSummary } from "@/domain/types/region";
import type {
  Site,
  SiteListItem,
  CreateSiteRequest,
  UpdateSiteRequest,
} from "@/domain/types/site";

import apiClient from "./client";
import { getWithEtag } from "./etag-cache";
import { fetchAllPages } from "./paginated-api";

export interface RegionListParams {
  page?: number;
  pageSize?: number;
  includeArchived?: boolean;
  parentCse?: string;
  siteSize?: string;
}

type RegionListResponse =
  | PaginatedResponse<RegionSummary>
  | RegionSummary[]
  | { items: RegionSummary[]; pagination?: PaginationInfo };

const DEFAULT_REGION_PAGE_SIZE = 100;

export async function getRegions(
  params: RegionListParams = {},
): Promise<PaginatedResponse<RegionSummary>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? DEFAULT_REGION_PAGE_SIZE;
  const includeArchived = params.includeArchived ?? false;
  const parentCse = params.parentCse?.trim();
  const siteSize = params.siteSize?.trim();
  const data = await getWithEtag<RegionListResponse>(
    "/api/regions",
    `regions:${page}:${pageSize}:${includeArchived}:${parentCse ?? ""}:${siteSize ?? ""}`,
    {
      params: {
        page,
        pageSize,
        includeArchived,
        ...(parentCse ? { parentCse } : {}),
        ...(siteSize ? { siteSize } : {}),
      },
    },
  );

  if (!Array.isArray(data) && "pagination" in data && data.pagination) {
    return data as PaginatedResponse<RegionSummary>;
  }

  const items = Array.isArray(data) ? data : data.items;
  return {
    items,
    pagination: {
      page,
      pageSize,
      totalCount: items.length,
      totalPages: items.length > 0 ? 1 : 0,
    },
  };
}

export interface SiteListParams {
  regionId?: string;
  siteSize?: string;
}

const DEFAULT_MEASURES_SUMMARY = { existingCount: 0, toBeImplementedCount: 0 };
const DEFAULT_MASTERY_RATE = 100;
const DEFAULT_AVERAGE_SCORE = 0;

function normalizeSite(site: Site): Site {
  return {
    ...site,
    measuresSummary: site.measuresSummary ?? DEFAULT_MEASURES_SUMMARY,
    overdueActionCount: site.overdueActionCount ?? 0,
    averageScore: site.averageScore ?? DEFAULT_AVERAGE_SCORE,
    masteryRate: site.masteryRate ?? DEFAULT_MASTERY_RATE,
    masteryByCategory: site.masteryByCategory ?? [],
  };
}

export async function getSites(
  params?: SiteListParams,
): Promise<SiteListItem[]> {
  const requestParams = params
    ? {
        ...params,
        siteSize: params.siteSize === "" ? undefined : params.siteSize,
      }
    : undefined;
  const sites = await fetchAllPages<SiteListItem>("/api/sites", requestParams);
  return sites.map((site) => ({
    ...site,
    measuresSummary: site.measuresSummary ?? DEFAULT_MEASURES_SUMMARY,
    overdueActionCount: site.overdueActionCount ?? 0,
  }));
}

export async function getSite(siteId: string): Promise<Site> {
  const { data } = await apiClient.get<Site>(`/api/sites/${siteId}`);
  return normalizeSite(data);
}

export async function createSite(body: CreateSiteRequest): Promise<Site> {
  const { data } = await apiClient.post<Site>("/api/sites", body);
  return normalizeSite(data);
}

export async function updateSite(
  siteId: string,
  body: UpdateSiteRequest,
): Promise<Site> {
  const { data } = await apiClient.patch<Site>(`/api/sites/${siteId}`, body, {
    headers: { "Content-Type": "application/merge-patch+json" },
  });
  return normalizeSite(data);
}

export async function archiveSite(siteId: string): Promise<void> {
  await apiClient.delete(`/api/sites/${siteId}`);
}
